import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { selectionItemOutlineClasses } from "@/lib/selection-item-styles";
import { toAbsoluteAssetUrl } from "@/lib/api-client";
import { Plus } from "lucide-react";
import type { ImageFolderPhoto, SubfolderType } from "@shared/api";
import { useLongPress } from "@/hooks/use-long-press";

/** Approximate row height (aspect-square cell + gap + optional label) for virtualizer */
const ROW_HEIGHT_ESTIMATE = 160;
/** Minimum photos to enable virtualization (below this use normal grid) */
const VIRTUALIZE_THRESHOLD = 24;

interface PhotoGridProps {
  photos: ImageFolderPhoto[];
  subfolderType: SubfolderType;
  /** Override section title (e.g. "Bemérésre – Garázová brána 1") */
  sectionTitle?: string;
  selectedPhotos: Set<string>;
  onSelectPhoto: (photoId: string, checked: boolean) => void;
  selectionMode: boolean;
  selectionModeEnabled: boolean;
  onPhotoClick: (photo: ImageFolderPhoto, index: number) => void;
  /** Mobile-only: enable long-press to enter selection mode. */
  isMobile?: boolean;
  /** Called on mobile long-press when selection is currently off. */
  onEnterSelectionMode?: (photoId: string) => void;
  onUploadClick?: () => void;
  onDropFiles?: (files: FileList, subfolderType: SubfolderType) => void;
  onDeletePhoto?: (photo: ImageFolderPhoto) => void;
  /** Optional label shown under each thumbnail (e.g. "Garážová brána 1-1") */
  getPhotoLabel?: (photo: ImageFolderPhoto) => string;
  /** When set and photos exceed VIRTUALIZE_THRESHOLD, grid is virtualized by row for performance */
  scrollRef?: React.RefObject<HTMLElement | null>;
  /** Number of columns per row when virtualized (default 6). Ignored when not virtualizing. */
  columnCount?: number;
}

const SUBFOLDER_LABELS: Record<SubfolderType, string> = {
  bemeres: "Bemérés",
  munkaink: "Munkáink",
  marketing: "Marketing",
};

const SECTION_STRIP_CLASS: Record<SubfolderType, string> = {
  bemeres: "border-l-red-500",
  munkaink: "border-l-green-500",
  marketing: "border-l-slate-400",
};

/** Single photo cell: thumbnail_url preferred, fallback to file_url; native lazy load */
function PhotoCell({
  photo,
  index,
  isSelected,
  selectionModeEnabled,
  onSelectPhoto,
  onPhotoClick,
  getPhotoLabel,
  isMobile,
  onEnterSelectionMode,
}: {
  photo: ImageFolderPhoto;
  index: number;
  isSelected: boolean;
  selectionModeEnabled: boolean;
  onSelectPhoto: (photoId: string, checked: boolean) => void;
  onPhotoClick: (photo: ImageFolderPhoto, index: number) => void;
  getPhotoLabel?: (photo: ImageFolderPhoto) => string;
  isMobile?: boolean;
  onEnterSelectionMode?: (photoId: string) => void;
}) {
  const label = getPhotoLabel?.(photo);
  const longPress = useLongPress({
    enabled: Boolean(isMobile && !selectionModeEnabled && onEnterSelectionMode),
    onLongPress: () => onEnterSelectionMode?.(photo.id),
  });
  return (
    <div
      data-photo-id={photo.id}
      className="relative group cursor-pointer flex flex-col gap-1"
    >
      <div
        className={cn(
          "aspect-square overflow-hidden rounded-md border border-border/20 relative transition-all",
          selectionModeEnabled
            ? "hover:shadow-md hover:border-border/35"
            : "hover:ring-2 hover:ring-primary/30 hover:shadow-md",
          selectionItemOutlineClasses(selectionModeEnabled, isSelected),
        )}
        {...longPress}
        onClick={(e) => {
          e.stopPropagation();
          if (selectionModeEnabled) {
            onSelectPhoto(photo.id, !isSelected);
          } else {
            onPhotoClick(photo, index);
          }
        }}
        onMouseDown={(e) => {
          if (selectionModeEnabled) e.stopPropagation();
        }}
      >
        <img
          src={toAbsoluteAssetUrl(photo.thumbnail_url || photo.file_url)}
          alt={photo.file_name}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            if (photo.thumbnail_url && photo.file_url) {
              e.currentTarget.src = toAbsoluteAssetUrl(photo.file_url);
            }
          }}
        />
        {isSelected && selectionModeEnabled && (
          <div className="absolute top-2 right-2 z-10 bg-background/90 rounded-full p-1 shadow-md pointer-events-none">
            <Checkbox checked={isSelected} className="h-4 w-4" disabled />
          </div>
        )}
      </div>
      {label != null && label !== "" && (
        <span className="text-xs text-muted-foreground truncate text-center px-0.5" title={label}>
          {label}
        </span>
      )}
    </div>
  );
}

export default function PhotoGrid({
  photos,
  subfolderType,
  sectionTitle,
  selectedPhotos,
  onSelectPhoto,
  selectionMode,
  selectionModeEnabled,
  onPhotoClick,
  isMobile,
  onEnterSelectionMode,
  onUploadClick,
  onDropFiles,
  onDeletePhoto,
  getPhotoLabel,
  scrollRef,
  columnCount = 6,
}: PhotoGridProps) {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && onDropFiles) {
      onDropFiles(e.dataTransfer.files, subfolderType);
    }
  };

  const title = sectionTitle ?? SUBFOLDER_LABELS[subfolderType];
  const totalItems = photos.length + (onUploadClick ? 1 : 0);
  const cols = columnCount;
  const rowCount = Math.ceil(totalItems / cols);
  const useVirtual = Boolean(scrollRef?.current && totalItems >= VIRTUALIZE_THRESHOLD);
  const listRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: useVirtual ? rowCount : 0,
    getScrollElement: () => scrollRef?.current ?? null,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: 2,
  });

  const virtualRows = useVirtual ? rowVirtualizer.getVirtualItems() : [];
  const totalVirtualHeight = useVirtual ? rowVirtualizer.getTotalSize() : 0;

  const gridClasses = "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9 2xl:grid-cols-11 gap-3";

  const renderPhoto = (photo: ImageFolderPhoto, index: number) => (
    <PhotoCell
      key={photo.id}
      photo={photo}
      index={index}
      isSelected={selectedPhotos.has(photo.id)}
      selectionModeEnabled={selectionModeEnabled}
      onSelectPhoto={onSelectPhoto}
      onPhotoClick={onPhotoClick}
      getPhotoLabel={getPhotoLabel}
      isMobile={isMobile}
      onEnterSelectionMode={onEnterSelectionMode}
    />
  );

  if (useVirtual && scrollRef?.current) {
    return (
      <div className="mb-8">
        <h3 className={cn("text-sm font-medium mb-3 pl-3 border-l-4", SECTION_STRIP_CLASS[subfolderType], "text-muted-foreground")}>{title}</h3>
        <div onDragOver={handleDragOver} onDrop={handleDrop} ref={listRef}>
          <div style={{ height: totalVirtualHeight, position: "relative" }}>
            {virtualRows.map((virtualRow) => {
              const startIndex = virtualRow.index * cols;
              return (
                <div
                  key={virtualRow.key}
                  className={gridClasses}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                    minHeight: ROW_HEIGHT_ESTIMATE,
                  }}
                >
                  {Array.from({ length: cols }, (_, c) => {
                    const flatIndex = startIndex + c;
                    if (flatIndex >= totalItems) return null;
                    if (onUploadClick && flatIndex === 0) {
                      return (
                        <button
                          key="upload"
                          type="button"
                          onClick={onUploadClick}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                          className="aspect-square overflow-hidden rounded-md border border-dashed border-border/40 bg-muted/20 hover:border-primary/40 hover:bg-muted/30 transition-colors flex items-center justify-center cursor-pointer focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                        >
                          <Plus className="h-6 w-6 text-muted-foreground/70" />
                        </button>
                      );
                    }
                    const photoIndex = onUploadClick ? flatIndex - 1 : flatIndex;
                    const photo = photos[photoIndex];
                    return photo ? renderPhoto(photo, photoIndex) : null;
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className={cn("text-sm font-medium mb-3 pl-3 border-l-4", SECTION_STRIP_CLASS[subfolderType], "text-muted-foreground")}>{title}</h3>
      <div onDragOver={handleDragOver} onDrop={handleDrop}>
        <div className={gridClasses}>
          {onUploadClick && (
            <button
              type="button"
              onClick={onUploadClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="aspect-square overflow-hidden rounded-md border border-dashed border-border/40 bg-muted/20 hover:border-primary/40 hover:bg-muted/30 transition-colors flex items-center justify-center cursor-pointer focus:outline-none focus-visible:outline-none focus-visible:ring-0"
            >
              <Plus className="h-6 w-6 text-muted-foreground/70" />
            </button>
          )}
          {photos.map((photo, index) => renderPhoto(photo, index))}
        </div>
      </div>
    </div>
  );
}
